import type { AttachmentCategory } from "@nlc/shared-types";

/**
 * Interface representing a media item that can be displayed in the viewer.
 */
export interface MediaItem {
  /**
   * The category of the attachment
   */
  category?: AttachmentCategory;

  /**
   * MIME type of the media item
   */
  extension: string;

  /**
   * Unique identifier for the media item
   */
  id: string;

  /**
   * Display name of the media item
   */
  name: string;

  /**
   * Full-resolution URL (used for downloads)
   */
  url: string;
}

/**
 * Props for the ImageModal component
 */
export interface MediaPreviewModalProps {
  /**
   * Alternative text for the displayed media
   */
  alt?: string;

  /**
   * ID of the currently selected item (for controlled navigation)
   */
  currentItemId?: string;

  /**
   * ID of the initially selected item (for uncontrolled navigation)
   */
  defaultSelectedItem?: string;

  /**
   * Array of media items to display in the modal
   */
  mediaItems: MediaItem[];

  /**
   * Callback function when the modal is closed
   */
  onClose: () => void;

  /**
   * Callback function when download button is clicked
   */
  onDownload?: (item: MediaItem) => void;

  /**
   * Callback function when feedback button is clicked
   */
  onFeedback?: (itemId: string) => void;

  /**
   * Callback function when navigation occurs (for controlled navigation)
   */
  onNavigate?: (itemId: string) => void;

  /**
   * Whether to use the preview variant of the media URL
   */
  usePreview?: boolean;
}

/**
 * Props for the modal header component
 */
export interface ModalHeaderProps {
  /**
   * Title text to display in the header
   */
  title: string;
}

/**
 * Props for the action buttons component
 */
export interface ActionButtonsProps {
  /**
   * The currently displayed media item
   */
  currentItem?: MediaItem;

  /**
   * Callback function when the close button is clicked
   */
  onClose: () => void;

  /**
   * Callback function when the download button is clicked
   */
  onDownload: () => void;

  /**
   * Callback function when the feedback button is clicked
   */
  onFeedback?: (itemId: string) => void;
}

/**
 * Props for the media content component
 */
export interface MediaContentProps {
  /**
   * Alternative text for the displayed media
   */
  alt: string;

  attachmentId: number;

  extension: string;

  /**
   * Source URL of the media to display
   */
  src: string;

  /**
   * Whether to use the preview variant of the media URL
   */
  usePreview?: boolean;
}

/**
 * Props for the navigation controls component
 */
export interface NavigationControlsProps {
  /**
   * Index of the currently displayed item
   */
  currentIndex: number;

  /**
   * Callback function when navigation buttons are clicked
   */
  onNavigate: (direction: Directions) => void;

  /**
   * Total number of items available for navigation
   */
  totalItems: number;
}

export enum Directions {
  NEXT = "next",
  PREV = "prev",
}
